import React, { useEffect, useRef, useState } from "react";

import PeopleAltOutlined from "@mui/icons-material/PeopleAltOutlined";
import Close from "@mui/icons-material/Close";

import PassengerTypes from "./PassengerTypes";
import CabinClasses from "./CabinClasses";
import { useSelector } from "react-redux";
import { searchFormSelector } from "redux/searchForm";

import { useTranslations } from "hooks/useTranslations";
import { MediaQuery, ITravellerType } from "@thekilroygroup/shared-ui";
import {
  Box,
  Collapse,
  TextField,
  InputAdornment,
  Drawer,
  IconButton,
  Popover,
  Button,
  Stack,
  SxProps,
  useTheme,
} from "@mui/material";

export default function Who() {
  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const inputRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const { passengers, cabinClass, tripType } = useSelector(searchFormSelector);
  const { translations } = useTranslations();
  const translationsWho = translations.search.who;
  const selectedCabinClass = translationsWho.cabinClasses.values.find(
    ({ id }) => id === cabinClass,
  );

  useEffect(() => {
    setInputText(inputFieldText());
  }, [passengers, selectedCabinClass]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const inputFieldText = () => {
    let inputText = [
      selectedCabinClass !== undefined ? `${selectedCabinClass.title}` : ``,
    ];
    for (const [key, value] of Object.entries(passengers)) {
      if (value > 0) {
        const t =
          translationsWho.passengerTypes.passengers[key as ITravellerType]
            .title;
        inputText.push(` ${t}: ${value}`);
      }
    }
    return inputText.join();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.code === "Space") {
      setOpen(true);
    }
  };

  const { input, inputField, cabinClassSelector, isMultiSpacer } = sxStyles;

  return (
    <Box sx={input}>
      <Collapse
        in={tripType === "MULTI_DEST"}
        easing={theme.transitions.easing.easeIn}
        timeout={theme.transitions.duration.shortest}
      >
        <Box sx={isMultiSpacer}></Box>
      </Collapse>
      <TextField
        label={translationsWho.label}
        value={inputText}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <PeopleAltOutlined />
            </InputAdornment>
          ),
        }}
        onKeyDown={handleKeyDown}
        variant="outlined"
        onClick={handleClickOpen}
        sx={inputField}
        color="secondary"
        ref={inputRef}
      />
      <MediaQuery max="md">
        <Drawer anchor="bottom" open={open} onClose={handleClose}>
          <IconButton className="closeButton" onClick={handleClose}>
            <Close fontSize="medium" color="primary" />
          </IconButton>
          <Stack sx={cabinClassSelector} flexDirection="column">
            <CabinClasses />
            <PassengerTypes />
            <Button
              variant="contained"
              size="large"
              color="primary"
              sx={{ width: "100%" }}
              onClick={handleClose}
            >
              {translationsWho.buttonText}
            </Button>
          </Stack>
        </Drawer>
      </MediaQuery>
      <MediaQuery min="md">
        <Popover
          open={open}
          anchorEl={inputRef.current}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Stack sx={cabinClassSelector} flexDirection="row-reverse">
            <CabinClasses />
            <PassengerTypes />
          </Stack>
        </Popover>
      </MediaQuery>
    </Box>
  );
}

const sxStyles: { [key: string]: SxProps } = {
  input: {
    my: 1,
    width: "100%",
    mr: { md: 1 },
  },
  inputField: { width: "100%", "& label": { textTransform: "capitalize" } },
  cabinClassSelector: {
    pt: { xs: 4, md: 3.5 },
    pr: { xs: 2 },
    pb: { xs: 3.5 },
    pl: { xs: 2, md: 2 },
    maxWidth: "800px",
  },
  isMultiSpacer: {
    height: { md: 27, xl: 33.5 },
  },
};
